'use client';

import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';

export interface ValidatedInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Рендер рядом с подписью (например, бейдж Felix) */
  labelSuffix?: React.ReactNode;
  isValid?: boolean;
  error?: string;
  hint?: string;
}

const ValidatedInput = React.forwardRef<HTMLInputElement, ValidatedInputProps>(
  ({ className, label, labelSuffix, isValid, error, hint, id: idProp, name: nameProp, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = idProp ?? generatedId;

    return (
      <div className="space-y-1.5">
        {(label || labelSuffix) && (
          <div className="flex items-center gap-2 flex-wrap">
            {label && (
              <Label
                htmlFor={inputId}
                className="text-sm font-semibold text-[#1A1A1A]"
              >
                {label}
              </Label>
            )}
            {labelSuffix}
          </div>
        )}
        
        <div className="relative">
          <input
            id={inputId}
            name={nameProp}
            className={cn(
              // Базовые стили - мягкий фон
              'flex w-full rounded-lg border bg-[#F5F5F5] px-4 text-base text-[#1A1A1A]',
              // Высота для touch-friendly (48px)
              'h-12',
              // Placeholder
              'placeholder:text-[#666666]',
              // Transition
              'transition-colors',
              // Focus - белый фон + более тёмная серая граница
              'focus:outline-none focus:bg-white focus:border-[#CCCCCC]',
              // Disabled
              'disabled:cursor-not-allowed disabled:bg-[#FAFAFA] disabled:text-[#999999]',
              // Padding для галочки
              isValid && 'pr-12',
              // Цвет границы в зависимости от состояния
              isValid 
                ? 'border-[#22C55E] bg-white' 
                : 'border-[#E5E5E5]',
              className
            )}
            ref={ref}
            {...props}
          />
          
          {/* Зелёная галочка при валидном поле */}
          {isValid && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Check 
                className="h-6 w-6 text-[#22C55E]" 
                strokeWidth={3}
              />
            </div>
          )}
        </div>

        {/* Текст ошибки или подсказки */}
        {error && (
          <p className="text-sm font-medium text-[#EF4444]">
            {error}
          </p>
        )}
        {hint && !error && (
          <p className="text-sm text-[#666666]">
            {hint}
          </p>
        )}
      </div>
    );
  }
);
ValidatedInput.displayName = 'ValidatedInput';

export { ValidatedInput };



